// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * DOM only. Not one byte of a user's file is interpreted in this file — it is
 * handed strings and structures the worker already produced.
 *
 * Every piece of recovered text reaches the page through `textContent` or a
 * `createTextNode`, never through `innerHTML`. A prompt is arbitrary text from
 * a file of unknown provenance; treating it as markup would let an image
 * rewrite this page.
 */

import { carrierWords } from './ops/report.ts';
import { afterIdatNote, describeChunkType } from './ops/graft.ts';
import { COMFY_READER_DATE } from './ops/verify.ts';
import { formatBytes } from './ingest.ts';
import { summariseRecipe, flagSensitiveTerms } from './recipe/summarise.ts';
import { term } from './glossary.ts';
import type { Finding, InspectResult, Recipe, VerifyReport } from './types.ts';

/* ── icons (Lucide, inlined — no font, no CDN) ─────────────────────── */

const PATHS: Record<string, string> = {
  sprout: '<path d="M7 20h10"/><path d="M10 20c5.5-2.5.8-6.4 3-10"/><path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z"/><path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z"/>',
  upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m17 8-5-5-5 5"/><path d="M12 3v12"/>',
  shield: '<path d="M20 13c0 5-3.5 7.5-7.7 8.9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1 1 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z"/>',
  alert: '<path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  check: '<path d="M21.8 10A10 10 0 1 1 17 3.3"/><path d="m9 11 3 3L22 4"/>',
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  x: '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
  download: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/>',
  copy: '<rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  share: '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="m16 6-4-4-4 4"/><path d="M12 2v13"/>',
  list: '<path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M3 6h.01"/><path d="M3 12h.01"/><path d="M3 18h.01"/>',
  rotate: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
  eye: '<path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0"/><circle cx="12" cy="12" r="3"/>',
};

export function icon(name: string, size = 16): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${PATHS[name] ?? ''}</svg>`;
}

function svgEl(name: string, size = 16): SVGElement {
  const wrap = document.createElement('div');
  wrap.innerHTML = icon(name, size);
  return wrap.firstElementChild as SVGElement;
}

/* ── shell ─────────────────────────────────────────────────────────── */

export function renderShell(root: HTMLElement) {
  root.innerHTML = `
    <header class="site-header">
      <span class="brand">${icon('sprout', 20)} regraft</span>
      <nav class="header-nav" aria-label="About this tool">
        <button class="nav-btn" type="button" data-modal="how">How it works</button>
        <button class="nav-btn" type="button" data-modal="dialects">Dialects</button>
        <button class="nav-btn" type="button" data-modal="privacy">Privacy</button>
        <button class="nav-btn" type="button" data-modal="about">About</button>
      </nav>
    </header>

    <main class="main-content">
      <section class="intro" id="intro">
        <h1>Get the prompt back out of an AI image</h1>
        <p class="lede">
          Discord, Reddit and a re-encode all strip the recipe out of a PNG — but often it is still
          there, in a place your reader did not look. regraft checks all five carriers, including
          ${term('lsb', 'the pixels themselves')}, and writes what it finds back into the file.
        </p>
        <button class="trust-badge" type="button" data-modal="privacy">
          ${icon('shield', 14)} Runs entirely in your browser. Your image is never uploaded.
        </button>
      </section>

      <section aria-labelledby="dz-title">
        <div class="dropzone" id="dropzone" role="button" tabindex="0"
             aria-describedby="dz-hint">
          ${icon('upload', 30)}
          <p class="dz-title" id="dz-title">Drop a PNG here</p>
          <p class="dz-hint" id="dz-hint">or tap to choose a file · paste with <kbd>Ctrl</kbd>/<kbd>⌘</kbd>+<kbd>V</kbd></p>
          <p class="dz-privacy">Nothing is uploaded. Files up to 512 MB, read entirely on this device.</p>
        </div>
        <input type="file" id="file-input" accept="image/png,.png" class="visually-hidden" tabindex="-1" />
        <div class="samples" id="samples">
          <span class="samples-label">Try a sample:</span>
        </div>
      </section>

      <section class="progress-panel" id="progress" hidden aria-live="polite">
        <div class="progress-row">
          <span class="progress-phase" id="progress-phase">Working</span>
          <span class="progress-count" id="progress-count"></span>
        </div>
        <div class="progress-track"><div class="progress-fill" id="progress-fill"></div></div>
      </section>

      <section class="error-panel" id="error" hidden role="alert">
        <p class="error-title">${icon('alert', 16)} <span id="error-title-text">That did not work</span></p>
        <p class="error-detail" id="error-detail"></p>
        <button class="btn btn-sm" type="button" id="error-retry">${icon('rotate', 14)} Try another file</button>
      </section>

      <div id="results" hidden></div>
    </main>

    <button class="log-toggle" type="button" id="log-toggle" aria-expanded="false" aria-label="Event log">
      ${icon('list', 15)}<span class="log-toggle-label">Event log</span><span id="log-count">0</span>
    </button>

    <footer class="site-footer">
      <div class="footer-inner">
        <span>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
      </div>
    </footer>

    <div id="modal-host"></div>
  `;
}

/* ── progress + error ──────────────────────────────────────────────── */

export function setProgress(phase: string, done: number, total: number) {
  const panel = document.getElementById('progress');
  const fill = document.getElementById('progress-fill');
  const label = document.getElementById('progress-phase');
  const count = document.getElementById('progress-count');
  if (!panel || !fill || !label || !count) return;
  panel.hidden = false;
  label.textContent = phase;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  fill.style.width = `${pct}%`;
  count.textContent = total > 1 ? `${pct}%` : '';
}

export function hideProgress() {
  const panel = document.getElementById('progress');
  if (panel) panel.hidden = true;
}

export function showError(message: string, title = 'That did not work') {
  const panel = document.getElementById('error');
  const detail = document.getElementById('error-detail');
  const heading = document.getElementById('error-title-text');
  if (!panel || !detail || !heading) return;
  heading.textContent = title;
  detail.textContent = message;
  panel.hidden = false;
}

export function hideError() {
  const panel = document.getElementById('error');
  if (panel) panel.hidden = true;
}

/* ── small builders ────────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function panel(title: string, extra?: HTMLElement): { root: HTMLElement; body: HTMLElement } {
  const root = el('section', 'panel');
  const head = el('div', 'panel-head');
  head.append(el('h2', 'panel-title', title));
  if (extra) head.append(extra);
  const body = el('div', 'panel-body');
  root.append(head, body);
  return { root, body };
}

function button(label: string, iconName: string, className = 'btn btn-sm'): HTMLButtonElement {
  const b = el('button', className);
  b.type = 'button';
  b.append(svgEl(iconName, 14), document.createTextNode(` ${label}`));
  return b;
}

/* ── the verdict ───────────────────────────────────────────────────── */

function verdict(result: InspectResult): HTMLElement {
  const root = el('div', 'verdict');
  const n = result.recipes.length;

  if (n === 0) {
    root.classList.add('is-empty');
    root.append(el('p', 'verdict-headline', 'No generation recipe in this file.'));
    const stealthSkipped = result.stealth?.skipped;
    root.append(
      el(
        'p',
        'verdict-sub',
        stealthSkipped
          ? `All four text carriers were checked and are empty. The pixel plane could not be checked: ${stealthSkipped} That proves nothing about where this image came from — chat apps, forums and every re-encode strip these on upload.`
          : 'All four text carriers and the pixel plane were checked, and all five are empty. That proves nothing about where this image came from — chat apps, forums and every re-encode strip these on upload, and a re-encode destroys the pixel plane too.',
      ),
    );
    return root;
  }

  root.classList.add('is-good');
  const hidden = result.recipes.filter((r) => r.carrier === 'pixel-lsb' || r.afterIdat || r.carrier === 'zTXt');
  root.append(
    el('p', 'verdict-headline', `Recovered ${n} recipe${n === 1 ? '' : 's'} from this image.`),
  );

  const where = new Map<string, number>();
  for (const r of result.recipes) where.set(r.carrier, (where.get(r.carrier) ?? 0) + 1);
  const parts = [...where.entries()].map(([c, k]) => `${k} in ${carrierWords(c)}`);

  let sub = `${parts.join(', ')}.`;
  if (hidden.length > 0) {
    sub += ` ${hidden.length} of them ${hidden.length === 1 ? 'sits' : 'sit'} somewhere a typical reader does not look.`;
  }
  root.append(el('p', 'verdict-sub', sub));
  return root;
}

/* ── recipe cards ──────────────────────────────────────────────────── */

export interface RecipeHandlers {
  onCopy: (recipe: Recipe) => void;
  onSave: (recipe: Recipe) => void;
  onSelectionChange: () => void;
}

const selected = new Set<string>();

export function selectedRecipeIds(): Set<string> {
  return selected;
}

function recipeCard(recipe: Recipe, handlers: RecipeHandlers): HTMLElement {
  const card = el('article', 'recipe-card');

  const head = el('div', 'recipe-head');
  const pick = el('label', 'recipe-pick');
  const box = document.createElement('input');
  box.type = 'checkbox';
  box.checked = selected.has(recipe.id);
  box.addEventListener('change', () => {
    if (box.checked) selected.add(recipe.id);
    else selected.delete(recipe.id);
    handlers.onSelectionChange();
  });
  const keyword = el('span', 'recipe-keyword', recipe.keyword);
  pick.append(box, keyword);
  head.append(pick);

  head.append(el('span', 'badge', recipe.generator));
  if (!recipe.certain) head.append(el('span', 'badge badge-guess', 'best guess'));
  if (recipe.carrier === 'pixel-lsb') head.append(el('span', 'badge badge-hidden', 'from the pixels'));
  else if (recipe.afterIdat) head.append(el('span', 'badge badge-hidden', 'after the image data'));
  else if (recipe.carrier === 'zTXt') head.append(el('span', 'badge badge-hidden', 'compressed'));

  head.append(
    el('span', 'recipe-carrier', `${recipe.label} · ${carrierWords(recipe.carrier)} · ${formatBytes(recipe.byteLength)}`),
  );
  card.append(head);

  const body = el('div', 'recipe-body');

  const summary = summariseRecipe(recipe);
  body.append(el('p', 'recipe-headline', summary.headline));

  if (summary.fields.length > 0) {
    const dl = el('dl', 'field-grid');
    for (const [k, v] of summary.fields) {
      dl.append(el('dt', undefined, k), el('dd', undefined, v));
    }
    body.append(dl);
  }

  const sensitive = flagSensitiveTerms(recipe.text);
  if (sensitive.length > 0) {
    const note = el('div', 'sensitive-note');
    note.append(svgEl('alert', 15));
    const text = el('div');
    text.append(document.createTextNode('This text names '));
    sensitive.slice(0, 5).forEach((s, i) => {
      if (i > 0) text.append(document.createTextNode(i === Math.min(sensitive.length, 5) - 1 ? ' and ' : ', '));
      text.append(document.createTextNode(`${s.kind} (`));
      text.append(el('code', undefined, s.sample));
      text.append(document.createTextNode(')'));
    });
    text.append(document.createTextNode('. Worth a look before you share the file.'));
    note.append(text);
    body.append(note);
  }

  const details = el('details', 'raw');
  const summaryEl = el('summary', undefined, `Show the ${recipe.kind === 'json' ? 'raw JSON' : 'raw text'}`);
  const pre = el('pre', 'raw-text');
  pre.textContent = recipe.kind === 'json' ? prettyJson(recipe.text) : recipe.text;
  details.append(summaryEl, pre);
  body.append(details);

  const actions = el('div', 'card-actions');
  const copyBtn = button('Copy', 'copy');
  copyBtn.addEventListener('click', () => handlers.onCopy(recipe));
  const saveBtn = button(`Save .${recipe.kind === 'json' ? 'json' : 'txt'}`, 'download');
  saveBtn.addEventListener('click', () => handlers.onSave(recipe));
  actions.append(copyBtn, saveBtn);
  body.append(actions);

  card.append(body);
  return card;
}

function prettyJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/* ── findings, chunk table, prediction ─────────────────────────────── */

const LEVEL_ICON: Record<string, string> = { good: 'check', warn: 'alert', bad: 'x', info: 'info' };

function findingsList(findings: Finding[]): HTMLElement {
  const list = el('ul', 'findings');
  for (const f of findings) {
    const li = el('li', 'finding');
    const mark = svgEl(LEVEL_ICON[f.level] ?? 'info', 15);
    mark.classList.add(`lv-${f.level}`);
    const text = el('div');
    text.append(el('span', 'finding-title', `${f.title}. `), el('span', 'finding-detail', f.detail));
    li.append(mark, text);
    list.append(li);
  }
  return list;
}

function chunkTable(result: InspectResult): HTMLElement {
  const scroll = el('div', 'table-scroll');
  const table = el('table', 'data');
  table.innerHTML =
    '<thead><tr><th>#</th><th>Type</th><th>What it is</th><th>Offset</th><th>Bytes</th><th>Checksum</th></tr></thead>';
  const tbody = el('tbody');
  for (const chunk of result.parse.chunks) {
    const tr = el('tr');
    tr.append(el('td', undefined, String(chunk.index)));
    const typeCell = el('td');
    typeCell.append(el('code', undefined, chunk.type));
    tr.append(typeCell);
    tr.append(el('td', undefined, describeChunkType(chunk.type) + afterIdatNote(chunk.type, chunk.afterIdat)));
    tr.append(el('td', undefined, chunk.offset.toLocaleString()));
    tr.append(el('td', undefined, chunk.dataLen.toLocaleString()));
    tr.append(el('td', chunk.crcOk ? 'cell-ok' : 'cell-bad', chunk.crcOk ? 'ok' : 'MISMATCH'));
    tbody.append(tr);
  }
  if (result.parse.trailerLength > 0) {
    const tr = el('tr');
    tr.append(el('td', undefined, '—'));
    tr.append(el('td', undefined, '—'));
    tr.append(el('td', 'cell-note', 'stray bytes after the end of the image'));
    tr.append(el('td', undefined, result.parse.trailerStart.toLocaleString()));
    tr.append(el('td', undefined, result.parse.trailerLength.toLocaleString()));
    tr.append(el('td', undefined, '—'));
    tbody.append(tr);
  }
  table.append(tbody);
  scroll.append(table);
  return scroll;
}

function comfyPanel(result: InspectResult): HTMLElement {
  const { root, body } = panel('What ComfyUI will see');
  const lead = el('p', 'action-note');
  lead.textContent = `A prediction from that application's own reader as it stood on ${COMFY_READER_DATE} — not a test against a running copy.`;
  body.append(lead);

  const list = el('ul', 'findings');
  const first = el('li', 'finding');
  const keys = result.comfy.sawKeys;
  const ok = result.comfy.completed && keys.length > 0;
  const mark = svgEl(ok ? 'check' : 'x', 15);
  mark.classList.add(ok ? 'lv-good' : 'lv-warn');
  const text = el('div');
  if (!result.comfy.completed) {
    text.append(el('span', 'finding-title', 'It will load nothing from this file.'));
  } else if (keys.length === 0) {
    text.append(el('span', 'finding-title', 'It will find no keys in this file.'));
  } else {
    text.append(el('span', 'finding-title', `It will find: ${keys.join(', ')}.`));
  }
  first.append(mark, text);
  list.append(first);

  for (const note of result.comfy.notes) {
    const li = el('li', 'finding');
    const m = svgEl('info', 15);
    m.classList.add('lv-info');
    li.append(m, el('div', 'finding-detail', note));
    list.append(li);
  }
  body.append(list);
  return root;
}

/* ── the results view ──────────────────────────────────────────────── */

export interface ResultHandlers extends RecipeHandlers {
  onReset: () => void;
  onGraft: () => void;
  onClear: () => void;
  onSaveReport: () => void;
  onSaveBundle: () => void;
  onAddSidecar: () => void;
}

export function renderResults(
  result: InspectResult,
  fileName: string,
  fileBytes: number,
  handlers: ResultHandlers,
): void {
  const host = document.getElementById('results');
  if (!host) return;

  selected.clear();
  // Pre-select everything: the overwhelmingly common intent is "put it all
  // back". An empty selection would make the primary button dead on arrival.
  for (const r of result.recipes) selected.add(r.id);

  const frag = document.createDocumentFragment();
  const wrap = el('div', 'results');

  // Head
  const head = el('div', 'result-head');
  const left = el('div');
  left.append(el('p', 'result-file', fileName));
  left.append(el('p', 'result-meta', `${result.imageInfo} · ${formatBytes(fileBytes)} · ${result.parse.chunks.length} chunks`));
  const resetBtn = button('Start over', 'rotate', 'btn btn-sm btn-ghost');
  resetBtn.addEventListener('click', handlers.onReset);
  head.append(left, resetBtn);
  wrap.append(head);

  wrap.append(verdict(result));

  // Recipes
  if (result.recipes.length > 0) {
    const recipes = el('div', 'recipes');
    for (const recipe of result.recipes) recipes.append(recipeCard(recipe, handlers));
    wrap.append(recipes);
  }

  // Actions
  const actions = panel('What now');
  const grid = el('div', 'action-grid');

  if (result.recipes.length > 0) {
    const graftNote = el('p', 'action-note');
    graftNote.innerHTML = `A ${term('graft')} writes the ticked recipes into the image as text chunks placed right after the header, replacing any older copy of the same keyword. The picture is copied across byte for byte — nothing is re-encoded.`;
    grid.append(graftNote);
  }

  const sidecar = el('div', 'sidecar-slot');
  sidecar.id = 'sidecar-slot';
  sidecar.append(document.createTextNode('No recipe file added. If you saved one earlier, add it here and it can be grafted in too.'));
  const addBtn = button('Add a recipe file', 'upload');
  addBtn.addEventListener('click', handlers.onAddSidecar);
  sidecar.append(addBtn);
  grid.append(sidecar);

  const row = el('div', 'action-row');
  const graftBtn = button('Write them back into the image', 'sprout', 'btn btn-primary');
  graftBtn.id = 'graft-btn';
  graftBtn.addEventListener('click', handlers.onGraft);
  row.append(graftBtn);

  const reportBtn = button('Save the report', 'download');
  reportBtn.addEventListener('click', handlers.onSaveReport);
  row.append(reportBtn);

  if (result.recipes.length > 0) {
    const bundleBtn = button('Save recipe.json', 'download');
    bundleBtn.addEventListener('click', handlers.onSaveBundle);
    row.append(bundleBtn);
  }

  if (result.stealth?.hit) {
    const clearBtn = button('Overwrite the hidden pixel plane', 'eye');
    clearBtn.addEventListener('click', handlers.onClear);
    row.append(clearBtn);
  }
  grid.append(row);

  const warn = el('p', 'action-note');
  warn.textContent =
    'A grafted file carries the recipe by design. Anything in it — prompts, model names, file paths — travels with the image from then on.';
  grid.append(warn);

  actions.body.append(grid);
  wrap.append(actions.root);

  // Output slot, filled after a graft or a clear
  const out = el('div');
  out.id = 'output-slot';
  wrap.append(out);

  wrap.append(comfyPanel(result));

  if (result.findings.length > 0) {
    const f = panel('Notes on this file');
    f.body.append(findingsList(result.findings));
    wrap.append(f.root);
  }

  const chunks = panel(`Chunk table`);
  chunks.body.append(chunkTable(result));
  wrap.append(chunks.root);

  frag.append(wrap);
  host.replaceChildren(frag);
  host.hidden = false;
  updateGraftButton();
}

export function updateGraftButton() {
  const btn = document.getElementById('graft-btn') as HTMLButtonElement | null;
  if (!btn) return;
  const n = selected.size + (sidecarCount > 0 ? sidecarCount : 0);
  btn.disabled = n === 0;
  btn.replaceChildren(
    svgEl('sprout', 14),
    document.createTextNode(
      n === 0 ? ' Tick a recipe to write it back' : ` Write ${n} back into the image`,
    ),
  );
}

let sidecarCount = 0;

export function setSidecarState(name: string | null, count: number) {
  sidecarCount = count;
  const slot = document.getElementById('sidecar-slot');
  if (!slot) return;
  const btn = slot.querySelector('button');
  slot.replaceChildren();
  if (name) {
    slot.classList.add('has-file');
    slot.append(
      document.createTextNode(`${name} — ${count} recipe${count === 1 ? '' : 's'} ready to graft.`),
    );
  } else {
    slot.classList.remove('has-file');
    slot.append(
      document.createTextNode('No recipe file added. If you saved one earlier, add it here and it can be grafted in too.'),
    );
  }
  if (btn) slot.append(btn);
  updateGraftButton();
}

/* ── the output of a graft or a clear ──────────────────────────────── */

export function renderGraftOutput(
  verify: VerifyReport,
  wrote: string[],
  onDownload: () => void,
  onShare: (() => void) | null,
): void {
  const slot = document.getElementById('output-slot');
  if (!slot) return;

  const { root, body } = panel('The new file');
  const ok = verify.idatIdentical && verify.badCrcCount === 0;

  const headline = el('div', ok ? 'verdict is-good' : 'verdict');
  headline.append(
    el(
      'p',
      'verdict-headline',
      ok ? `Written: ${wrote.join(', ')}.` : 'Written, but the check did not pass — see below.',
    ),
  );
  headline.append(
    el(
      'p',
      'verdict-sub',
      verify.idatIdentical
        ? `Re-read with a fresh parser: the image data is byte-for-byte identical to what you put in. ${verify.outputBytes.toLocaleString()} bytes out, from ${verify.inputBytes.toLocaleString()} in.`
        : 'Re-read with a fresh parser: the image data does NOT match the input. Do not use this file.',
    ),
  );
  body.append(headline);

  const list = el('ul', 'findings');
  const add = (level: string, title: string, detail: string) => {
    const li = el('li', 'finding');
    const m = svgEl(LEVEL_ICON[level] ?? 'info', 15);
    m.classList.add(`lv-${level}`);
    const t = el('div');
    t.append(el('span', 'finding-title', `${title}. `), el('span', 'finding-detail', detail));
    li.append(m, t);
    list.append(li);
  };

  add(
    verify.badCrcCount === 0 ? 'good' : 'bad',
    verify.badCrcCount === 0 ? 'Every checksum in the new file matches' : `${verify.badCrcCount} checksums do not match`,
    `${verify.chunkCount} chunks were written.`,
  );

  const keys = verify.comfy.sawKeys;
  add(
    verify.comfy.completed && keys.length > 0 ? 'good' : 'warn',
    verify.comfy.completed && keys.length > 0
      ? `ComfyUI's reader will find ${keys.join(', ')}`
      : "ComfyUI's reader will find nothing in this file",
    `Predicted from that reader as it stood on ${COMFY_READER_DATE}.`,
  );
  for (const note of verify.comfy.notes) add('info', 'Note', note);

  if (verify.trailerLength > 0) {
    add('info', `${verify.trailerLength} stray bytes after the end of the image were kept`, 'They are somebody else’s data and are left alone.');
  }
  body.append(list);

  const row = el('div', 'action-row');
  const dl = button('Download the image', 'download', 'btn btn-primary');
  dl.addEventListener('click', onDownload);
  row.append(dl);
  if (onShare) {
    const sh = button('Share', 'share');
    sh.addEventListener('click', onShare);
    row.append(sh);
  }
  body.append(row);

  slot.replaceChildren(root);
  root.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

export function renderClearOutput(
  changed: number,
  total: number,
  mode: string,
  sizeDelta: number,
  onDownload: () => void,
  onShare: (() => void) | null,
): void {
  const slot = document.getElementById('output-slot');
  if (!slot) return;
  const { root, body } = panel('The overwritten image');

  const headline = el('div', 'verdict is-good');
  headline.append(el('p', 'verdict-headline', 'The hidden pixel plane has been overwritten.'));
  headline.append(
    el(
      'p',
      'verdict-sub',
      `${changed.toLocaleString()} of ${total.toLocaleString()} samples in the ${
        mode === 'alpha' ? 'alpha channel' : 'red, green and blue channels'
      } changed, each by one level out of 256 — invisible, but the picture is not the same file any more. The image data was re-compressed, so the size changed by ${
        sizeDelta >= 0 ? '+' : ''
      }${formatBytes(Math.abs(sizeDelta))}.`,
    ),
  );
  body.append(headline);

  const note = el('p', 'action-note');
  note.textContent =
    'This removes only the hidden pixel plane. Text chunks, EXIF, colour profiles and content credentials are untouched — regraft is not a metadata remover.';
  body.append(note);

  const row = el('div', 'action-row');
  const dl = button('Download the image', 'download', 'btn btn-primary');
  dl.addEventListener('click', onDownload);
  row.append(dl);
  if (onShare) {
    const sh = button('Share', 'share');
    sh.addEventListener('click', onShare);
    row.append(sh);
  }
  body.append(row);

  slot.replaceChildren(root);
  root.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/* ── transient toast, for copy confirmations ───────────────────────── */

export function toast(message: string) {
  const live = document.getElementById('live-region');
  if (live) live.textContent = message;
}
